import React, { useState, useMemo, useEffect } from 'react'

import { EditOutlined, LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { useImport } from '@hooks/shared/useImport'
import ImportModal from '@shared/ImportModal'
import Can from '@shared/permissions/Can'
import useCan from '@shared/permissions/useCan'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import { Tag, Button, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { downloadImportTemplate } from '@utils/downloadTemplate'
import type { Permission } from '@/permissions_manifest.generated'

interface InboundListViewProps {
  service: any
  supplierService: any
  addJob: (...args: any[]) => void
  role?: 'admin' | 'operator'
  showMerchantColumn?: boolean
  importModalVisible?: boolean
  setImportModalVisible?: (v: boolean) => void
}

const InboundListView: React.FC<InboundListViewProps> = ({
  service,                    // Service Layer (New)
  supplierService,            // 供應商 service：載入下拉清單給供應商 filter 使用
  addJob,                     // BackgroundJobManager 的 addJob 函數 (必須)
  role = 'admin',             // 角色標識：'admin' 或 'operator' (可選，預設為 admin)
  showMerchantColumn = false,     // 是否顯示貨主欄位 (Operator 需要)
  importModalVisible: externalImportModalVisible,
  setImportModalVisible: externalSetImportModalVisible,
}) => {
  const { t, i18n } = useTranslation()
  const can = useCan()
  const navigate = useNavigate()
  const [lastModified, setLastModified] = useState(0)
  const [internalImportModalVisible, setInternalImportModalVisible] = useState(false)

  // Support external or internal import modal control
  const importModalVisible = externalImportModalVisible !== undefined ? externalImportModalVisible : internalImportModalVisible
  const setImportModalVisible = externalSetImportModalVisible || setInternalImportModalVisible

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)
  // Get merchants from OperatorDataContext (only for operator role)
  // Always call hooks unconditionally
  const operatorData = useOperatorData()
  const merchants = role === 'operator' ? (operatorData?.merchants || []) : []

  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string }>>([])
  useEffect(() => {
    let cancelled = false
    supplierService.getRecords({ page: 1, page_size: 100 })
      .then((res: { success?: boolean; data?: Array<{ id: string; name: string }> }) => {
        if (cancelled) return
        if (res?.success && res.data) setSuppliers(res.data)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        console.error('Failed to load suppliers for inbound list filter:', error)
      })
    return () => { cancelled = true }
  }, [supplierService])

  // 匯入功能（使用 Context 共享背景任務管理器）
  // useImport 是 .js 模組，JSDoc 上 @param {string} typeParamName 會讓 TS 把第一個 arg 誤推為 string；
  // 與其他 .tsx caller (ProductsList / BundlesListView) 一致用 `as any` 繞過 .js 邊界。
  const handleImport = (useImport as any)({
    action: (formData: any) => service.import(formData),
    addJob,
    jobName: 'shared.inbound_list.import.job_name',
    // 注意：不在 onSuccess 重新載入表格，因為 Background Job 還在執行中
    // 使用者需要在 Background Job 完成後手動重新整理，或等待自動通知
  })

  // 處理 Modal 匯入
  const handleModalImport = async (file: any, merchantId: any, overrideExisting: any) => {
    await handleImport(file, merchantId, overrideExisting)
  }

  // 處理下載範例
  const handleDownloadTemplate = async (merchantId: any) => {
    await downloadImportTemplate({
      service,
      language: i18n.language,
      merchantId,
      t,
      successKey: 'shared.inbound_list.template.success',
      errorKey: 'shared.inbound_list.template.error'
    })
  }
  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        nameKey: `models.inbound.name`,
        persist: true,
        sortable: true,
        sortFieldName: 'inbounds.name',
        width: '180px',
        wrap: true,
        cell: ({ id, name }: any) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/inbounds/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`/${role}/inbounds/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
    ]
    // Operator 需要顯示貨主欄位
    if (showMerchantColumn) {
      baseColumns.push({
        nameKey: `models.inbound.merchant`,
        width: '120px',
        wrap: true,
        selector: ({ merchant_name }: any) => merchant_name || '-',
      })
    }
    baseColumns.push(
      {
        nameKey: `models.inbound.status`,
        width: '100px',
        cell: ({ status }: any) => {
          let color = 'default'
          if (status === 'pending') color = 'blue'
          else if (status === 'receiving') color = 'geekblue'
          else if (status === 'received') color = 'cyan'
          else if (status === 'putting_away') color = 'orange'
          else if (status === 'done') color = 'green'
          else if (status === 'canceled') color = 'red'
          return (
            <Tag color={color}>
              {t(`enums.inbound.status.${status}`)}
            </Tag>
          )
        },
      },
      {
        nameKey: `models.inbound.inbound_type`,
        width: '120px',
        cell: ({ inbound_type }: any) => {
          const colors: Record<string, string> = {
            receiving: 'blue',
            sales_return: 'orange',
            production: 'green'
          }
          return (
            <Tag color={colors[inbound_type] || 'default'}>
              {t(`enums.inbound.type.${inbound_type}`)}
            </Tag>
          )
        },
      },
      {
        nameKey: `models.inbound.eta`,
        sortable: true,
        sortFieldName: 'inbounds.eta',
        width: '120px',
        wrap: true,
        selector: ({ eta }: any) => eta ? dayjs(eta).format('YYYY-MM-DD') : '-',
      },
      {
        nameKey: 'models.inbound.supplier',
        width: '150px',
        wrap: true,
        selector: ({ supplier }: any) => supplier?.name || '-',
      },
      {
        nameKey: `models.inbound.warehouse`,
        width: '120px',
        wrap: true,
        selector: ({ warehouse_name }: any) => warehouse_name,
      },
      {
        name: (
          <Space size={4}>
            {t('models.inbound.total_items')}
            <Tooltip title={t('shared.inbound_list.column_tooltips.total_items')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '100px',
        selector: ({ total_items }: any) => total_items || 0,
      },
      {
        name: (
          <Space size={4}>
            {t('models.inbound_item.scheduled_quantity')}
            <Tooltip title={t('shared.inbound_list.column_tooltips.scheduled_quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '120px',
        selector: ({ scheduled_quantity }: any) => scheduled_quantity || 0,
      },
      {
        name: (
          <Space size={4}>
            {t('models.inbound_item.putaway_quantity')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.inbound_list.column_tooltips.putaway_quantity')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '120px',
        cell: ({ putaway_quantity, scheduled_quantity }: any) => {
          const received = putaway_quantity || 0
          const scheduled = scheduled_quantity || 0
          const percentage = scheduled > 0 ? ((received / scheduled) * 100).toFixed(0) : 0
          let color = '#999'
          if (received === 0) color = '#999'
          else if (received >= scheduled) color = '#52c41a'
          else if (received >= scheduled * 0.5) color = '#faad14'
          else color = '#ff4d4f'
          return (
            <span style={{ color }}>
              {received} ({percentage}%)
            </span>
          )
        },
      },
      {
        name: (
          <Space size={4}>
            {t('models.inbound_item.received_quantity')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.inbound_list.column_tooltips.received_quantity')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '120px',
        cell: ({ received_quantity, scheduled_quantity }: any) => {
          const verified = received_quantity || 0
          const scheduled = scheduled_quantity || 0
          const percentage = scheduled > 0 ? ((verified / scheduled) * 100).toFixed(0) : 0
          let color = '#999'
          if (verified === 0) color = '#999'
          else if (verified >= scheduled) color = '#52c41a'
          else if (verified >= scheduled * 0.5) color = '#faad14'
          else color = '#ff4d4f'
          return (
            <span style={{ color }}>
              {verified} ({percentage}%)
            </span>
          )
        },
      },
      {
        nameKey: `models.inbound.received_at`,
        sortable: true,
        sortFieldName: 'inbounds.received_at',
        width: '150px',
        wrap: true,
        selector: ({ received_at }: any) => received_at,
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'inbounds.created_at',
        width: '150px',
        wrap: true,
        selector: ({ created_at }: any) => created_at,
      },
      {
        nameKey: 'common.table.actions',
        width: '100px',
        cell: ({ id, editable }: any) => {
          // 使用 API 返回的 editable 標誌
          if (!editable) {
            return <span style={{ color: '#999' }}>-</span>
          }
          return (
            <Space size="small">
              <Can requiredPermission="inbounds:write">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/inbounds/${id}/edit`)}
                >
                  {t('common.button.edit')}
                </Button>
              </Can>
            </Space>
          )
        },
      }
    )
    return baseColumns
  }, [t, role, showMerchantColumn])
  const supportRules = useMemo(() => {
    const baseRules: any[] = [
      {
        label: t(`models.inbound.name`),
        component: MultipleValue,
        inputName: 'inbounds.name',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.name.title'),
          content: t('shared.inbound_list.filter_tooltips.name.content'),
        },
      },
    ]
    // Operator 需要貨主篩選
    if (showMerchantColumn) {
      baseRules.push({
        label: t(`models.inbound.merchant`),
        component: MultipleValue,
        inputName: 'inbounds.merchant_id',
        createable: false,
        defaultOptions: merchants.map((merchant: any) => ({
          label: merchant.name,
          value: merchant.id
        })),
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.merchant.title'),
          content: t('shared.inbound_list.filter_tooltips.merchant.content'),
        },
      })
    }
    baseRules.push(
      {
        label: t(`models.inbound.status`),
        component: MultipleValue,
        inputName: 'inbounds.status',
        createable: false,
        defaultOptions: [
          { label: t(`enums.inbound.status.pending`), value: 'pending' },
          { label: t(`enums.inbound.status.receiving`), value: 'receiving' },
          { label: t(`enums.inbound.status.received`), value: 'received' },
          { label: t(`enums.inbound.status.putting_away`), value: 'putting_away' },
          { label: t(`enums.inbound.status.done`), value: 'done' },
          { label: t(`enums.inbound.status.canceled`), value: 'canceled' },
        ],
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.status.title'),
          content: t('shared.inbound_list.filter_tooltips.status.content'),
        },
      },
      {
        label: t(`models.inbound.inbound_type`),
        component: MultipleValue,
        inputName: 'inbounds.inbound_type',
        createable: false,
        defaultOptions: [
          { label: t(`enums.inbound.type.receiving`), value: 'receiving' },
          { label: t(`enums.inbound.type.sales_return`), value: 'sales_return' },
          { label: t(`enums.inbound.type.production`), value: 'production' },
        ],
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.inbound_type.title'),
          content: t('shared.inbound_list.filter_tooltips.inbound_type.content'),
        },
      },
      {
        label: t('models.inbound.supplier'),
        component: MultipleValue,
        inputName: 'inbounds.supplier_id',
        createable: false,
        defaultOptions: suppliers.map(s => ({ label: s.name, value: s.id })),
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.supplier.title'),
          content: t('shared.inbound_list.filter_tooltips.supplier.content'),
        },
      },
      {
        label: t(`models.inbound_item.serial_number`),
        component: MultipleValue,
        inputName: 'serial_number',
        createable: true,
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.serial_number.title'),
          content: t('shared.inbound_list.filter_tooltips.serial_number.content'),
        },
      },
      {
        label: t(`models.inbound_item.product_sku`),
        component: MultipleValue,
        inputName: 'sku',
        createable: true,
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.product_sku.title'),
          content: t('shared.inbound_list.filter_tooltips.product_sku.content'),
        },
      },
      {
        label: t(`models.inbound.eta`),
        component: FullDayRangePicker,

        defaultPinned: true,
        sortable: false,
        inputName: 'inbounds.eta',
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.eta.title'),
          content: t('shared.inbound_list.filter_tooltips.eta.content'),
        },
      },
      {
        label: t(`models.inbound.received_at`),
        component: FullDayRangePicker,

        sortable: false,
        inputName: 'inbounds.received_at',
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.received_at.title'),
          content: t('shared.inbound_list.filter_tooltips.received_at.content'),
        },
      },
      {
        label: t(`common.created_at`),
        component: FullDayRangePicker,

        sortable: false,
        inputName: 'inbounds.created_at',
        tooltip: {
          title: t('shared.inbound_list.filter_tooltips.created_at.title'),
          content: t('shared.inbound_list.filter_tooltips.created_at.content'),
        },
      }
    )
    return baseRules
  }, [t, showMerchantColumn, merchants, suppliers])
  const actions = useMemo(() => {
    const all: Array<{ name: string; className: string; requiredPermission?: Permission; do: (selected: Iterable<any>) => void }> = [
      {
        name: `common.button.export`,
        className: 'btn btn-primary btn-outline mb-0',
        requiredPermission: 'inbounds:export',
        do: (selected) => executeBatchOperation({
          task: () => service.export(Array.from(selected), i18n.language),
          jobName: 'common.button.export'
        })
      },
    ]
    return all.filter(item => !item.requiredPermission || can(item.requiredPermission))
  }, [executeBatchOperation, service, i18n.language, can])

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={{
          match: 'prefix',
          searchFields: [
            t('models.inbound.name'),
            t('models.inbound.barcode'),
            t('models.inbound.extra_name'),
            t('models.inbound_item.product_sku'),
            t('models.product.barcode'),
          ],
        }}
        columnsEditable={true}
        tableId={`${role}InboundsTable`}
        defaultQuery={{
          sorting_field: 'inbounds.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={actions}
        service={service}
        filterHelpExample={t('shared.inbound_list.filter_help_example')}
        filterDocsUrl={`/docs/${role}/inbounds/list#features`}
      />
      <ImportModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onImport={handleModalImport}
        onDownloadTemplate={handleDownloadTemplate}
        role={role}
        merchants={merchants}
        title={t('shared.inbound_list.import.modal_title')}
      />
    </>
  )
}

export default InboundListView
